import { useMemo } from 'react';
import type { CreditAnalysis, CreditAnalysisStatus } from '@/api/commercial/credit-analysis';
import { CreditAnalysisStatus as StatusEnum } from '@/api/commercial/credit-analysis';
import { ProposalStatus } from '@/api/commercial/proposals';

interface ExpirationValidation {
  isExpired: boolean;
  shouldUpdateStatus: boolean;
  suggestedStatus: CreditAnalysisStatus;
  reason: string | null;
}

/**
 * Hook para validar o vencimento de análise de crédito (V-BE-02)
 *
 * Regras:
 * 1. Se newAnalysisIn for uma data no passado: status deve ser EXPIRED
 * 2. Se newAnalysisIn === 'NEXT_PURCHASE': depende do status da proposta
 *
 * @param creditAnalysis - Análise de crédito a validar
 * @param proposalStatus - Status da proposta (opcional, para NEXT_PURCHASE validation)
 */
export function useCreditAnalysisExpiration(
  creditAnalysis: CreditAnalysis | null | undefined,
  proposalStatus?: string,
): ExpirationValidation {
  return useMemo(() => {
    if (!creditAnalysis) {
      return {
        isExpired: false,
        shouldUpdateStatus: false,
        suggestedStatus: StatusEnum.NOT_STARTED,
        reason: null,
      };
    }

    const { status, newAnalysisIn } = creditAnalysis;

    // Se já está EXPIRED, não precisa validar novamente
    if (status === StatusEnum.EXPIRED) {
      return {
        isExpired: true,
        shouldUpdateStatus: false,
        suggestedStatus: StatusEnum.EXPIRED,
        reason: 'Análise já está marcada como vencida',
      };
    }

    // Se não tem newAnalysisIn configurado, não está vencida
    if (!newAnalysisIn) {
      return {
        isExpired: false,
        shouldUpdateStatus: false,
        suggestedStatus: status,
        reason: null,
      };
    }

    // Caso 1: newAnalysisIn é 'NEXT_PURCHASE'
    if (newAnalysisIn === 'NEXT_PURCHASE') {
      // Verifica se a proposta foi finalizada ou cancelada
      const isProposalFinalized =
        proposalStatus === ProposalStatus.FINISHED ||
        proposalStatus === ProposalStatus.CANCELED ||
        proposalStatus === 'DECLINED';

      if (isProposalFinalized) {
        return {
          isExpired: true,
          shouldUpdateStatus: true,
          suggestedStatus: StatusEnum.EXPIRED,
          reason: 'Próxima análise configurada para "na próxima venda" e a proposta foi finalizada',
        };
      }

      return {
        isExpired: false,
        shouldUpdateStatus: false,
        suggestedStatus: status,
        reason: null,
      };
    }

    // Caso 2: newAnalysisIn é uma data
    try {
      const analysisDate = new Date(newAnalysisIn);
      const today = new Date();

      // Zerar as horas para comparar apenas as datas
      today.setHours(0, 0, 0, 0);
      analysisDate.setHours(0, 0, 0, 0);

      if (analysisDate < today) {
        return {
          isExpired: true,
          shouldUpdateStatus: true,
          suggestedStatus: StatusEnum.EXPIRED,
          reason: `Data de próxima análise (${analysisDate.toLocaleDateString('pt-BR')}) já passou`,
        };
      }

      if (analysisDate.getTime() === today.getTime()) {
        return {
          isExpired: true,
          shouldUpdateStatus: true,
          suggestedStatus: StatusEnum.EXPIRED,
          reason: 'Data de próxima análise é hoje',
        };
      }

      return {
        isExpired: false,
        shouldUpdateStatus: false,
        suggestedStatus: status,
        reason: null,
      };
    } catch {
      // Erro ao parsear a data
      return {
        isExpired: false,
        shouldUpdateStatus: false,
        suggestedStatus: status,
        reason: null,
      };
    }
  }, [creditAnalysis, proposalStatus]);
}

/**
 * Utilitário para verificar se um status é "Em dia" (não vencido)
 */
export function isAnalysisStatusInGoodStanding(status: CreditAnalysisStatus): boolean {
  return (
    status === StatusEnum.APPROVED ||
    status === StatusEnum.PARTIALLY_APPROVED ||
    status === StatusEnum.IN_ANALYSIS
  );
}

/**
 * Utilitário para verificar se uma análise está vencida baseado no status
 */
export function isAnalysisExpired(status: CreditAnalysisStatus): boolean {
  return status === StatusEnum.EXPIRED;
}
